import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { renderAsync } from 'docx-preview';
import { X, Download, Save, Briefcase, AlertTriangle, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { API_BASE } from '../config';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// const API_BASE = "http://127.0.0.1:8000";


export default function CandidateDetails({ resume, onClose, searchedSkills = [] }) {
    const [numPages, setNumPages] = useState(null);
    const [pdfError, setPdfError] = useState(null);

    // Unified File State
    const [blobUrl, setBlobUrl] = useState(null);
    const [fileBlob, setFileBlob] = useState(null);
    const [loading, setLoading] = useState(true);

    // Initial guess from filename, but will be authoritative via MIME type
    const initialIsPdf = (resume.original_filename || resume.name || "").toLowerCase().endsWith('.pdf');
    const [isPdf, setIsPdf] = useState(initialIsPdf);

    const fileUrl = `${API_BASE}/resume/${resume._id}/file`;

    const docxContainerRef = useRef(null);

    // 1. Fetch File as Blob (for BOTH PDF and DOCX) to bypass IDM
    useEffect(() => {
        let active = true;
        setLoading(true);

        const fetchFile = async () => {
            try {
                const res = await axios.get(fileUrl, { responseType: 'blob' });
                if (active) {
                    const blob = res.data;
                    setFileBlob(blob);
                    setBlobUrl(URL.createObjectURL(blob));

                    // ROBUSTNESS: Check actual MIME type
                    if (blob.type === 'application/pdf') {
                        setIsPdf(true);
                    } else if (blob.type.includes('word') || blob.type.includes('officedocument')) {
                        setIsPdf(false);
                    }

                    setLoading(false);
                }
            } catch (e) {
                console.error("Failed to fetch file blob", e);
                setPdfError(e);
                setLoading(false);
            }
        };
        fetchFile();
        return () => {
            active = false;
        };
    }, [fileUrl]);

    // Cleanup Blob URL
    useEffect(() => {
        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    // 2. Render DOCX when blob is ready
    useEffect(() => {
        if (!isPdf && fileBlob && docxContainerRef.current) {
            docxContainerRef.current.innerHTML = ''; // Clear previous
            renderAsync(fileBlob, docxContainerRef.current, docxContainerRef.current, {
                className: "docx-viewer",
                inWrapper: true,
                ignoreWidth: false, // FALSE keeps the page structure intact
                experimental: true
            })
                .then(() => {
                    console.log("DOCX rendered");
                    // Apply Highlighting to DOCX DOM
                    if (searchedSkills.length > 0) {
                        highlightDocxContent(docxContainerRef.current, searchedSkills);
                    }
                })
                .catch(e => {
                    console.error("DOCX render failed", e);
                    setPdfError(e);
                });
        }
    }, [fileBlob, isPdf, searchedSkills]);

    const highlightDocxContent = (container, skills) => {
        if (!container || !skills.length) return;
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
        const nodes = [];
        let node;
        while (node = walker.nextNode()) nodes.push(node);

        const regex = new RegExp(`(${skills.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');

        nodes.forEach(textNode => {
            const parent = textNode.parentNode;
            if (!parent || parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') return;

            const content = textNode.nodeValue;
            if (regex.test(content)) {
                const fragment = document.createDocumentFragment();
                let lastIdx = 0;
                content.replace(regex, (match, p1, offset) => {
                    fragment.appendChild(document.createTextNode(content.substring(lastIdx, offset)));
                    const mark = document.createElement('mark');
                    mark.className = "bg-brand-500/40 text-brand-900 dark:text-brand-100 rounded px-0.5 border-b-2 border-brand-600";
                    mark.textContent = match;
                    fragment.appendChild(mark);
                    lastIdx = offset + match.length;
                });
                fragment.appendChild(document.createTextNode(content.substring(lastIdx)));
                parent.replaceChild(fragment, textNode);
            }
        });
    };

    const pdfTextRenderer = (textItem) => {
        if (!searchedSkills.length) return textItem.str;

        const regex = new RegExp(`(${searchedSkills.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
        if (!regex.test(textItem.str)) return textItem.str;

        // react-pdf customTextRenderer wants a string with HTML-like segments if we aren't careful, 
        // but it's actually safer to just return the string and use CSS if possible.
        // HOWEVER, react-pdf text layer is made of spans. we can highlight them.
        return textItem.str;
    };


    // Mock edit state
    const [formData, setFormData] = useState({
        name: resume.name,
        email: resume.email,
        phone: resume.phone || "",
        summary: resume.summary || "",
        skills: resume.skills || [],
        years: resume.years_experience || 0,
        education: resume.education || [],
        work_experience: resume.work_experience || []
    });

    // Sync state with props when resume changes
    useEffect(() => {
        if (resume) {
            // Initial load from props
            setFormData({
                name: resume.name,
                email: resume.email,
                phone: resume.phone || "",
                summary: resume.summary || "",
                skills: resume.skills || [],
                years: resume.years_experience || 0,
                education: resume.education || [],
                work_experience: resume.work_experience || []
            });

            // If data is incomplete (e.g. from partial view or fallback), fetch full details
            if (resume.email === 'Loading...' || !resume.summary) {
                const fetchFullDetails = async () => {
                    try {
                        console.log("Fetching full details for", resume._id);
                        const res = await axios.get(`${API_BASE}/resume/${resume._id || resume.resume_id}`);
                        const fullData = res.data;

                        setFormData(prev => ({
                            ...prev,
                            name: fullData.name || prev.name, // Keep existing name if valid
                            email: fullData.email,
                            phone: fullData.phone || "",
                            summary: fullData.summary || "",
                            skills: fullData.skills || [],
                            years: fullData.years_experience || 0,
                            education: fullData.education || [],
                            work_experience: fullData.work_experience || []
                        }));
                    } catch (err) {
                        console.error("Failed to fetch full candidate details", err);
                    }
                };
                fetchFullDetails();
            }

            // Also reset PDF/File state for the new candidate
            // (The fileUrl depends on resume._id, so the existing file effect will trigger automatically)
            console.log("Candidate Data Loaded:", resume);
        }
    }, [resume]);

    const handleSave = () => {
        toast.success("Changes saved (Mock)");
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 20 }}
                    className="bg-white dark:bg-slate-900 w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl flex overflow-hidden border border-white/10"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Left Panel: Candidate Info */}
                    <div className="w-1/3 border-r border-slate-200 dark:border-slate-800 flex flex-col bg-white dark:bg-slate-900">

                        {/* Header */}
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                            <div className="w-16 h-16 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center text-2xl font-bold mb-4 dark:bg-brand-900/30 dark:text-brand-400">
                                {formData.name ? formData.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white">{formData.name}</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{formData.email}</p>
                            {formData.phone && <p className="text-sm text-slate-500 dark:text-slate-400">{formData.phone}</p>}
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">

                            {/* AI Fit Insight justification */}
                            {resume.fitReason && (
                                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-xl relative overflow-hidden group">
                                    <h4 className="text-[10px] font-bold text-purple-700 dark:text-purple-300 uppercase tracking-widest mb-2 flex items-center gap-1">
                                        <span className="text-xs">✨</span> AI Fit Insight
                                    </h4>
                                    <p className="text-xs text-purple-900 dark:text-purple-100 italic leading-relaxed">
                                        "{resume.fitReason}"
                                    </p>
                                </div>
                            )}

                            {/* Summary */}
                            {formData.summary && (
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Summary</label>
                                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                                        {searchedSkills.length > 0 ? (
                                            formData.summary.split(new RegExp(`(${searchedSkills.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi')).map((part, i) => {
                                                const isMatch = searchedSkills.some(s => s.toLowerCase() === part.toLowerCase());
                                                return isMatch ? (
                                                    <mark key={i} className="bg-brand-500/30 text-brand-900 dark:text-brand-100 rounded px-0.5 font-medium border-b border-brand-500">
                                                        {part}
                                                    </mark>
                                                ) : part;
                                            })
                                        ) : formData.summary}
                                    </p>
                                </div>
                            )}

                            {/* Work Experience Timeline */}
                            {formData.work_experience?.length > 0 && (
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Work History</label>
                                    <div className="space-y-3">
                                        {formData.work_experience.map((job, i) => (
                                            <div key={i} className="relative pl-4 border-l-2 border-slate-200 dark:border-slate-700">
                                                <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-slate-300 dark:bg-slate-600"></div>
                                                <h4 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{job.role || "Role"}</h4>
                                                <p className="text-xs text-brand-600 dark:text-brand-400 font-medium">{job.company}</p>
                                                <p className="text-[10px] text-slate-400">{job.duration}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Education */}
                            {formData.education?.length > 0 && (
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Education</label>
                                    <div className="space-y-2">
                                        {formData.education.map((edu, i) => (
                                            <div key={i} className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg text-sm">
                                                <div className="font-semibold text-slate-800 dark:text-slate-200">{edu.degree}</div>
                                                <div className="text-slate-500 dark:text-slate-400 text-xs">{edu.school} ({edu.year})</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Experience Stats */}
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Total Experience</label>
                                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                                    <Briefcase className="w-5 h-5 text-slate-400" />
                                    <input
                                        type="number"
                                        value={formData.years}
                                        onChange={(e) => setFormData({ ...formData, years: e.target.value })}
                                        className="bg-transparent outline-none w-full text-slate-800 dark:text-slate-200 font-medium"
                                    />
                                    <span className="text-sm text-slate-400">Years</span>
                                </div>
                            </div>

                            {/* Skills */}
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Skills</label>
                                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 min-h-[100px]">
                                    <div className="flex flex-wrap gap-2">
                                        {(() => {
                                            const sortedSkills = [...formData.skills].sort((a, b) => {
                                                const aMatch = searchedSkills.some(s => s.toLowerCase() === a.toLowerCase());
                                                const bMatch = searchedSkills.some(s => s.toLowerCase() === b.toLowerCase());
                                                if (aMatch && !bMatch) return -1;
                                                if (!aMatch && bMatch) return 1;
                                                return 0;
                                            });
                                            return sortedSkills.map((skill, i) => {
                                                const isHighlighted = searchedSkills.some(s => s.toLowerCase() === skill.toLowerCase());
                                                return (
                                                    <span
                                                        key={i}
                                                        className={clsx(
                                                            "px-2 py-1 rounded-md border text-xs flex items-center gap-1 transition-all",
                                                            isHighlighted
                                                                ? "bg-brand-600 text-white border-brand-600 shadow-sm shadow-brand-500/50 font-medium scale-105"
                                                                : "bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                                                        )}
                                                    >
                                                        {skill}
                                                        <button className={clsx("hover:text-red-500", isHighlighted ? "text-white/80" : "")}><X className="w-3 h-3" /></button>
                                                    </span>
                                                );
                                            });
                                        })()}
                                        <button className="px-2 py-1 rounded-md bg-brand-50 text-brand-600 border border-brand-200 text-xs font-medium hover:bg-brand-100 dark:bg-brand-900/20 dark:border-brand-900/30 dark:text-brand-400">
                                            + Add
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* DEBUG DATA */}


                        {/* Footer Actions */}
                        <div className="p-6 border-t border-slate-100 dark:border-slate-800 flex gap-3">
                            <button
                                onClick={handleSave}
                                className="flex-1 bg-brand-600 hover:bg-brand-700 text-white py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                            >
                                <Save className="w-4 h-4" /> Save
                            </button>
                        </div>
                    </div>

                    {/* Right Panel: Content Viewer */}
                    <div className="w-2/3 bg-slate-100 dark:bg-slate-950 flex flex-col relative">
                        <div className="absolute top-4 right-4 z-10 flex gap-2">
                            <a
                                href={fileUrl}
                                download
                                className="p-2 bg-white/90 backdrop-blur text-slate-700 rounded-lg hover:bg-white shadow-sm transition-colors dark:bg-slate-800/90 dark:text-slate-200"
                                title="Download Original"
                            >
                                <Download className="w-5 h-5" />
                            </a>
                            <button
                                onClick={onClose}
                                className="p-2 bg-white/90 backdrop-blur text-slate-700 rounded-lg hover:bg-white shadow-sm transition-colors hover:text-red-500 dark:bg-slate-800/90 dark:text-slate-200 dark:hover:text-red-400"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-auto flex flex-col bg-slate-100/50 dark:bg-slate-950">

                            {/* Loading State */}
                            {loading && !pdfError && (
                                <div className="flex-1 flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mb-4"></div>
                                    <p className="text-slate-500">Securely loading document...</p>
                                </div>
                            )}

                            {/* DOCX Viewer - Full Width */}
                            {!isPdf && !loading && !pdfError && (
                                <div className="flex-1 w-full bg-white shadow-sm overflow-x-hidden text-slate-900">
                                    {/* docx-preview renders children here */}
                                    <div ref={docxContainerRef} />
                                </div>
                            )}

                            {/* Error State */}
                            {pdfError && (
                                <div className="text-center mt-20 max-w-md">
                                    <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Preview Unavailable</h3>
                                    <p className="text-slate-500 dark:text-slate-400 mt-2">
                                        This file type cannot be rendered differently <br /> (Try downloading it).
                                    </p>
                                    <a href={fileUrl} className="mt-4 inline-flex items-center gap-2 text-brand-600 hover:underline">
                                        Download File <ExternalLink className="w-4 h-4" />
                                    </a>
                                </div>
                            )}

                            {/* PDF Viewer - Only render if we have a BLOB (bypasses IDM) */}
                            {isPdf && blobUrl && !loading && (
                                <Document
                                    file={blobUrl}
                                    onLoadSuccess={({ numPages }) => { setNumPages(numPages); setPdfError(null); }}
                                    onLoadError={(e) => setPdfError(e)}
                                    loading={null}
                                >
                                    {Array.from(new Array(numPages || 0), (el, index) => (
                                        <Page
                                            key={`page_${index + 1}`}
                                            pageNumber={index + 1}
                                            scale={1.1}
                                            className="mb-4 shadow-lg"
                                            renderTextLayer={true}
                                            renderAnnotationLayer={false}
                                            customTextRenderer={({ str, itemIndex }) => {
                                                if (!searchedSkills.length) return str;
                                                const regex = new RegExp(`(${searchedSkills.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
                                                return str.split(regex).map((part, i) => {
                                                    if (regex.test(part)) {
                                                        return `<mark class="pdf-highlight">${part}</mark>`;
                                                    }
                                                    return part;
                                                }).join('');
                                            }}
                                        />
                                    ))}
                                </Document>
                            )}
                        </div>
                    </div>

                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
